/**
 * Groups container files into the collapsible year sections used by the invoices and reports
 * containers.
 *
 * Pattern and flags mirror App\Support\DocumentImport\Importer::extractImportInformationFromFileName,
 * so the frontend classifies a name exactly like the import that produced it. Everything else -
 * hand-uploaded files above all - lands in one trailing undated group.
 *
 * The grouper returns descriptors and never touches the files themselves. Prefixing file.name in
 * place, from inside the computed property that had just read it, is what made the second render
 * see an already prefixed name, fail the pattern and drop every file into the undated group.
 *
 * Keep this module free of imports: tests/Feature/FileContainerDateGroupingTest.php evaluates this
 * source as a standalone ES module under node.
 */
const IMPORT_NAME_PATTERN = /^(?<customerNumber>[\d\S]+?)_(?<year>[\d]+?)_(?<month>[\d]+?)-(?<filename>.*)$/miu;

const UNDATED_GROUP_KEY = 'other';

/**
 * @param {Array<{name: string}>} files
 * @returns {Array<{key: string, year: (string|null), isUndated: boolean, items: Array<{file: object, displayName: string, month: (number|null)}>}>}
 */
export function groupFilesByImportDate(files) {
    const years = new Map();
    const undated = [];

    (files || []).forEach((file) => {
        const matches = IMPORT_NAME_PATTERN.exec(file.name);

        if (matches === null) {
            undated.push({file: file, displayName: file.name, month: null});

            return;
        }

        const year = matches.groups.year;
        const month = matches.groups.month;

        if (!years.has(year)) {
            years.set(year, []);
        }

        // The label keeps the whole stored name behind the month, so a document stays recognisable
        // by the name it was imported under.
        years.get(year).push({
            file: file,
            displayName: '[' + month + '] ' + file.name,
            month: Number(month),
        });
    });

    // Newest year first, and newest month first inside a year - the files relation carries no
    // ORDER BY, so the backend sequence is not one to rely on.
    const groups = Array.from(years.keys())
        .sort((a, b) => Number(b) - Number(a))
        .map((year) => ({
            key: year,
            year: year,
            isUndated: false,
            items: years.get(year).sort((a, b) => b.month - a.month),
        }));

    if (undated.length > 0) {
        groups.push({key: UNDATED_GROUP_KEY, year: null, isUndated: true, items: undated});
    }

    return groups;
}
